import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  ThemeProvider,
  Toolbar,
  Typography,
  createTheme,
} from "@mui/material";
import { ArrowBack, Edit } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { getClientById } from "../../../api/clientApi";
import { formatDate, formatDateTime } from "../../../services/formatDate";

const defaultTheme = createTheme({
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
});

interface FieldMappings {
  [key: string]: string;
}
const fieldMappings: FieldMappings = {
  telefone_contato_cliente: "Telefone",
  documento_identificador_cliente: "Documento",
  data_recebimento_veiculo: "Data de Recebimento",
  codigo_radio_veiculo: "Código de Rádio",
  data_armazenagem_veiculo: "Data de Armazenagem",
  descricao_cor: "Cor",
  km_veiculo: "Km Inicial",
  ano_veiculo: "Ano do Veículo",
  km_final_veiculo: "Km Final",
  descricao_modelo: "Modelo",
  descricao_local: "Local de Armazenagem",
  descricao_ano_modelo: "Ano/Modelo",
  created_at: "Data de Criação",
  empresa_nome: "Empresa Localizada",
  permite_fotos_veiculo: "Permite uso das Fotos",
  placa_veiculo: "Placa do Veículo"
};

const ViewClientPage: React.FC = () => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const [clientData, setClientData] = useState<any>(null);

  useEffect(() => {
    const fetchClientData = async () => {
      try {
        if (token && clientId !== undefined) {
          const data = await getClientById(token, clientId);
          setClientData(data);
        } else {
          console.error("Token não encontrado ou clientId é indefinido");
        }
      } catch (error) {
        console.error("Erro ao buscar detalhes do cliente:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [token, clientId]);

  const handleEditClient = (clientId: number) => {
    navigate(`/editarCliente/${clientId}`);
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ mb: 2, mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  cursor: "pointer",
                  color: "red",
                },
              }}
              onClick={() => navigate("/veiculos")}
            >
              <IconButton
                edge="start"
                color="inherit"
                aria-label="voltar"
                sx={{ color: "inherit" }}
              >
                <ArrowBack />
              </IconButton>
              <Typography
                variant="h6"
                component="div"
                sx={{ color: "inherit", mt: 0.2 }}
              >
                Voltar
              </Typography>
            </Box>
          </Toolbar>

          <Container sx={{ mb: 4 }} maxWidth={"lg"}>
            <Box component="form" noValidate>
              <Typography
                variant="h5"
                fontStyle={"inherit"}
                sx={{ fontWeight: 400 }}
              >
                Veículo Vinculado
                <Divider
                  variant="middle"
                  sx={{
                    width: 120,
                    backgroundColor: "red",
                    height: 2,
                    marginLeft: 0,
                  }}
                />
              </Typography>
              <Grid sx={{ mt: 3 }} p={2} mr={4}>
                {loading ? (
                  <>
                    <Skeleton
                      variant="rectangular"
                      height={150}
                      sx={{ borderRadius: 5 }}
                    />
                    <Skeleton
                      variant="rectangular"
                      height={300}
                      sx={{ borderRadius: 5, mt: 3 }}
                    />
                  </>
                ) : (
                  <Paper
                    elevation={4}
                    sx={{ p: 2, borderRadius: 5, width: "100%" }}
                  >
                    {clientData && (
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="h5" mr={2}>
                            Nome:
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              typography: { sm: "h5", xs: "h6" },
                            }}
                          >
                            {clientData.nome_cliente}
                          </Typography>
                          <IconButton
                            aria-label="editar"
                            onClick={() => handleEditClient(clientData.id)}
                            sx={{ ml: "auto" }}
                          >
                            <Edit />
                          </IconButton>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Grid container spacing={2}>
                          {Object.entries(clientData).map(([key, value]) => {
                            const mappedFieldName = fieldMappings[key] || key;

                            const excludedFields = [
                              "id",
                              "id_empresa",
                              "nome_cliente",
                              "veiculos",
                              "veiculos_relacao",
                              "id_empresa",
                              "transferencia_veiculo",
                              "codigo_dms_cliente",
                              "recusa_veiculo",
                            ];

                            if (!excludedFields.includes(key)) {
                              let formattedValue: string;
                              if (typeof value === "string") {
                                formattedValue = value;
                              } else {
                                formattedValue = String(value);
                              }

                              return (
                                <Grid item xs={12} sm={6} md={6} key={key}>
                                  <Typography sx={{ fontWeight: "bold" }}>
                                    {mappedFieldName}:
                                  </Typography>
                                  <Typography variant="body1">
                                    {formattedValue}
                                  </Typography>
                                </Grid>
                              );
                            }
                            return null;
                          })}
                        </Grid>
                      </Box>
                    )}
                  </Paper>
                )}
              </Grid>

              {clientData &&
                Object.values(clientData.veiculos_relacao).map(
                  (vehicle: any, index: number) => (
                    <Grid key={index} p={2} mr={4}>
                      <Paper
                        elevation={4}
                        sx={{ p: 2, mb: 2, borderRadius: 5, width: "100%" }}
                      >
                        {vehicle && (
                          <Box
                            sx={{ display: "flex", flexDirection: "column" }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Typography variant="h5" mr={2}>
                                Chassi:
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  typography: { sm: "h5", xs: "h6" },
                                }}
                              >
                                {vehicle.chassi_veiculo}
                              </Typography>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={2}>
                              {Object.entries(vehicle).map(([key, value]) => {
                                const mappedFieldName =
                                  fieldMappings[key] || key;

                                const excludedFields = [
                                  "id",
                                  "id_cliente",
                                  "acoes",
                                  "fotos_km",
                                  "fotos_codigo_radio",
                                  "id_cor",
                                  "id_modelo",
                                  "id_ano_modelo",
                                  "id_local",
                                  "entrega_veiculo",
                                  "chassi_veiculo",
                                  "ano_veiculo",
                                  "nome_cliente",
                                  "cliente",
                                  "id_empresa",
                                  "transferencia_veiculo",
                                  "tem_checklist_agrupado"
                                ];

                                if (
                                  !excludedFields.includes(key) &&
                                  value !== null &&
                                  value !== ""
                                ) {
                                  let formattedValue: string;
                                  if (typeof value === "string") {
                                    if (key === "data_recebimento_veiculo") {
                                      formattedValue = formatDateTime(value);
                                    } else if (
                                      key === "data_armazenagem_veiculo"
                                    ) {
                                      formattedValue = formatDate(value);
                                    } else if (key === "created_at") {
                                      formattedValue = formatDateTime(value);
                                    }else if(typeof value === "boolean"){
                                      formattedValue = value ? 'Sim' : 'Não';
                                    } else {
                                      formattedValue = value;
                                    }
                                  } else {
                                    formattedValue = String(value);
                                  }

                                  return (
                                    <Grid item xs={12} sm={6} md={6} key={key}>
                                      <Typography sx={{ fontWeight: "bold" }}>
                                        {mappedFieldName}:
                                      </Typography>
                                      <Typography variant="body1">
                                        {formattedValue}
                                      </Typography>
                                    </Grid>
                                  );
                                } 
                                return null;
                              })}
                            </Grid>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  )
                )}
            </Box>
            <Box mt={2} textAlign="center">
              {loading ? (
                <Skeleton
                  variant="rectangular"
                  height={50}
                  sx={{ borderRadius: 5 }}
                />
              ) : (
                <Button
                  variant="contained"
                  sx={{
                    height: 50,
                    marginTop: 6,
                    backgroundColor: "#303030",
                    borderRadius: 2,
                    alignItems: "center",
                    ":hover": {
                      bgcolor: "#5c5c5c",
                    },
                  }}
                  fullWidth
                  onClick={() => navigate(`/veiculos/${clientId}`)}
                >
                  Adicionar ou Vincular Novo Veículo
                </Button>
              )}
            </Box>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ViewClientPage;
